import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styles from './ModernIncidentDetailPage.module.css';
import IncidentDetailHeader from './IncidentDetailHeader';
import IncidentDetailsSection from './IncidentDetailsSection';
import IncidentTimeline from './IncidentTimeline';
import IncidentReportedImpacts from './IncidentReportedImpacts';
import PagerDutyActivity from './PagerDutyActivity';
import IncidentArtifactLinks from './IncidentArtifactLinks';
import IncidentExecSummary from './IncidentExecSummary';
import useIncidentArtifacts from '../../../hooks/useIncidentArtifacts';
import { useIncidentDetails } from '../Incidents/hooks/useIncidentDetails';
import { isResolvedStatus } from '../Status/statusUtils';
import { canEditIncidentArtifacts } from '../../../utils/privilegeHelpers';
import { useSetNavActiveSection } from '../Layout/NavActiveSectionContext';
import { ConfigurationAPI } from '../../../connections/Configuration';
import { trackIncidentDetailPageView, trackPastIncidentDetailView, trackPastIncidentTimeSpent } from '../../../utils/modernUITracking';
import { trackUniqueVisitor, initializeEngagementTracking } from '../../../utils/newrelicTracking';

/**
 * ModernIncidentDetailPage
 *
 * Modern UI incident detail page, rendered when the "modern_ui" feature toggle is ON.
 * The legacy Viewer.js renders instead when the toggle is OFF — that switch is handled
 * in AppWrapper.js, not here.
 *
 * This component is the content layer only — ModernLayout wraps it at the routing level,
 * the same pattern as ModernHome.js.
 *
 * US1866098 scope: Header section only.
 * US1889423 scope: Incident Details section (bridge, chat channel, metadata grid).
 * Future stories will add timeline, notes, impact reports, etc. below the header.
 *
 * @param {Object} props
 * @param {Object} props.user - Authenticated user object (passed through from AppWrapper)
 * @param {Object} props.newrelic - NewRelic browser agent instance (passed through from AppWrapper)
 */
function ModernIncidentDetailPage({ user, newrelic }) {
  // The `:id` param is the problem number (e.g. "PRB0001234" or a temporary number)
  const { id } = useParams();

  // enabled=true immediately — unlike the accordion use-case on the home page,
  // the detail page always needs the full data right away.
  const { data, loading, error, refresh } = useIncidentDetails(id, true);

  // Data-driven nav highlight (Approach A).
  // Publish which top-level section this problem belongs to so the persistent
  // ModernHeader nav highlights the right tab on /problem/view/:id (a route that
  // matches neither NavLink). Source of truth mirrors `isResolved` below:
  // data.currentStatus === 'Resolve' → resolved → 'past'; otherwise 'current'.
  //   - Decision B: highlight NEITHER while status is unknown (section=null),
  //     i.e. while loading with no data yet, or on error / not-found.
  //   - Decision C: allow the highlight to migrate if status changes while
  //     viewing — this effect re-runs whenever data/loading/error change.
  const setNavActiveSection = useSetNavActiveSection();
  useEffect(() => {
    if (error) {
      setNavActiveSection(null);
    } else if (data) {
      // 'Resolve' is the canonical resolved marker the backend emits for resolved
      // problems, so real resolved problems are always caught here. ANY other status
      // — including a missing/unknown one — intentionally maps to 'current' (not a
      // silent fallthrough). This is the same determination the page's `isResolved`
      // below makes (non-'Resolve' → active/editable); diverging would let the nav
      // highlight contradict the detail page.
      const isResolved = isResolvedStatus(data.currentStatus);
      setNavActiveSection(isResolved ? 'past' : 'current');
    } else {
      // loading && !data → status unknown
      setNavActiveSection(null);
    }
    // Clear the published section on unmount so the highlight does not leak
    // onto / , /past-incidents, /profile, etc. when navigating away.
    return () => setNavActiveSection(null);
  }, [data, loading, error, setNavActiveSection]);

  // Fetch the app configuration — used for ServiceNow deep-links (snowHost).
  // Falls back to safe defaults on failure. (PagerDuty deep-links get their
  // hostname from the /pd-activity response, not from here.)
  const [snowHost, setSnowHost] = useState('https://cox.service-now.com');
  useEffect(() => {
    ConfigurationAPI.get()
      .then(config => {
        if (config?.serviceNowHost) {
          setSnowHost(config.serviceNowHost);
        }
      })
      .catch(() => {
        // keep safe defaults on failure
      });
  }, []);

  // Track detail page view for usage analytics
  useEffect(() => {
    if (id) {
      trackIncidentDetailPageView(id, newrelic);
    }
  }, [id, newrelic]);

  // Track past incident detail view and time spent once we know the incident is resolved.
  // Guards:
  //   - !loading: don't fire for new id while stale data from previous incident is in place
  //   - data?.number === id: identity check closes a race on A→B nav where the parent
  //     re-renders with new id before useIncidentDetails has cleared stale data
  //     (without this, the effect emits DetailView(B) using A's status, then a spurious
  //     ~0ms TimeSpent(B) when the hook then clears data and re-runs the effect).
  //     Uses `number` (the record identity — PRB or INC), not `prbNumber`, which the
  //     mapper leaves empty for INC-keyed records (would suppress resolved-INC tracking).
  useEffect(() => {
    if (!id || loading || data?.currentStatus !== 'Resolve' || data?.number !== id) return;
    const startTime = Date.now();
    trackPastIncidentDetailView(id, newrelic);
    return () => {
      trackPastIncidentTimeSpent(id, Date.now() - startTime, newrelic);
    };
  }, [id, loading, data?.currentStatus, data?.number, newrelic]);

  // Track unique visitor (once per problem per day) and engagement time on the
  // detail page — mirrors Viewer.js pattern.  Kept as a separate effect from
  // trackIncidentDetailPageView above; both share the same [id, newrelic] deps.
  // Cleanup fires the ProblemEngagement event when the user navigates away.
  useEffect(() => {
    if (!id || !newrelic) return;

    trackUniqueVisitor(newrelic, id);
    const tracker = initializeEngagementTracking(newrelic, id);
    return () => {
      if (tracker?.cleanup) tracker.cleanup();
    };
  }, [id, newrelic]);

  // US2174775: the one fetch of the Exec Summary Slide and Document links, shared by the
  // Executive Summary row (everyone) and the editor (Eternium). Past, non-archived incidents
  // only, which is `isPastIncident` below, computed inline because a hook must run before the
  // early returns that follow. `data.number === id` keeps the render after a navigation, which
  // still holds the previous incident's data, from asking for the new incident's links.
  const artifacts = useIncidentArtifacts(
    id,
    !!id && !!data && data.number === id && isResolvedStatus(data.currentStatus) && !data.archived
  );

  if (error) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.errorState} role="alert">
          <p className={styles.errorMessage}>
            Unable to load incident details.
          </p>
          <button className={styles.retryButton} onClick={refresh}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // PRB statuses are "Log & Classify", "Diagnose", and "Resolve" — backend types.OutageStatusResolve
  // isResolved: !data treated as read-only until status is confirmed (avoids flash on initial
  // load; safe during background refresh — stale data stays valid while loading flips true).
  // isPastIncident: strict — never propagate "loading" as "past" to tracking (would mislabel
  // Timeline.Expand on an active incident as section='past_incidents' during the load window).
  // US2072496: resolved-ness goes through the shared statusUtils.isResolvedStatus
  // predicate rather than an inline literal, so this page, the incident row, the
  // card and the detail header can never disagree about what "resolved" means.
  // The predicate still matches only the canonical "Resolve" (tolerating case and
  // surrounding whitespace), so the deliberate strictness described above holds:
  // any other status — including a missing one — remains active/editable.
  const isResolved = !data || isResolvedStatus(data.currentStatus);
  const isPastIncident = !!data && isResolvedStatus(data.currentStatus);
  const showBridge = !isResolved && !!data?.pagerDutyID;

  return (
    <div className={styles.pageContainer}>
      <div className={styles.incidentCard}>
        <IncidentDetailHeader
          shortDescription={data?.shortDescription}
          priority={data?.priority}
          status={data?.currentStatus}
          affectedBrand={data?.affectedBrand}
          clientImpact={data?.clientImpact}
          incidentStart={data?.incidentStart}
          incidentEnd={data?.incidentEnd}
          nextUpdate={data?.nextUpdate}
          loading={loading}
          user={user}
          problemId={id}
          statusFullDescription={data?.statusFullDescription}
          statusDescription={data?.statusDescription}
        />
        <div className={styles.sectionDivider} />
        <h2 className={styles.incidentDetailsHeader}>Incident Details</h2>
        <IncidentDetailsSection
          problemId={id}
          showBridge={showBridge}
          slackChannel={data?.slackChannel}
          incidentCommander={data?.incidentCommander}
          majorIncidentManager={data?.majorIncidentManager}
          incidentNumber={data?.incidentNumber}
          awscase={data?.awscase}
          prbNumber={data?.prbNumber}
          lastUpdated={data?.lastUpdated}
          loading={loading}
          snowHost={snowHost}
          newrelic={newrelic}
          isResolved={isResolved}
        />
        <IncidentExecSummary
          problemId={id}
          links={artifacts.links}
          loading={artifacts.loading}
          error={artifacts.error}
          newrelic={newrelic}
        />
        {/*
          US2163904: isPastIncident, not isResolved. isResolved is true while the page is still
          loading, which would render this control for an active incident. Archived incidents are
          refused by the server, so the control is not offered for them. The hook above is
          disabled for the same cases, so the editor is also never shown stale links.
        */}
        {id && isPastIncident && !data?.archived && canEditIncidentArtifacts(user) && (
          <>
            <div className={styles.sectionDivider} />
            <IncidentArtifactLinks
              key={id}
              problemId={id}
              links={artifacts.links}
              loading={artifacts.loading}
              loadError={artifacts.error}
              onRetry={artifacts.reload}
              onLinkChange={artifacts.setLink}
              newrelic={newrelic}
            />
          </>
        )}
        {id && data?.outageKey && (
          <>
            <div className={styles.sectionDivider} />
            <PagerDutyActivity
              problemID={id}
              outageID={data.outageKey}
              isActive={!isResolved}
            />
          </>
        )}
        <div className={styles.sectionDivider} />
        {/*
          US1950081: isTemporary is conservative on `!data` for the same reason isResolved is
          above. This page has no `if (loading)` early return, so the FIRST render of
          /incident/PRBT0001234 happens with data === null. Reading `!!data?.temporary` there
          yields false, which enables the impacts fetch and issues the exact
          GET /api/v1/problems/{PRBT}/impacts that handler_impact.go:372 refuses with 403 —
          the request this story exists to remove, on the primary entry point for a PRBT link.
          Assume temporary until the record says otherwise; one render later the real flag
          arrives and a non-temporary incident fetches normally.

          detailLoading closes the cost of that assumption: with fetching disabled the hook
          reports loading=false / impacts=[], so without it the section would render
          "No impacts reported yet." for the whole detail-fetch window rather than for one
          frame. `!data` is exactly coincident with the isTemporary guard window.
        */}
        <IncidentReportedImpacts
          problemId={id}
          user={user}
          onImpactCreated={refresh}
          snowHost={snowHost}
          isResolved={isResolved}
          isTemporary={!data || !!data.temporary}
          detailLoading={!data}
        />
        <div className={styles.sectionDivider} />
        <IncidentTimeline
          timelineEvents={data?.timelineEvents}
          loading={loading}
          incidentId={id}
          isPastIncident={isPastIncident}
          newrelic={newrelic}
        />
      </div>
    </div>
  );
}

export default ModernIncidentDetailPage;
